import { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Nav from 'react-bootstrap/Nav';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { FaListOl, FaDollarSign, FaArrowUp, FaArrowDown, FaEquals, FaGripLinesVertical } from 'react-icons/fa';

import { formatCurrencyShort, parseCurrencyToNumber, formatDateBucket, transformBreakdownToDonutData, generateDateBuckets, getAutoResolution } from '../../../common/utils';
import ThirdFloorDataGrid from '../../Grids';


const Leaderboards = ( { data, filters, contacts } ) => {
    const [ loaded, setLoaded ] = useState( false );
    const [ partners, setPartners ] = useState( { leads: [], pas: [], funded: [], volume: [] } );
    const [ teams, setTeams ] = useState( { leads: [], pas: [], funded: [], volume: [] } );
    const [ companies, setCompanies ] = useState( { leads: [], pas: [], funded: [], volume: [] } );
    const [ summaryData, setSummaryData ] = useState( {
        partners: {},
        teams: {},
        companies: {},
        totalLeads: 0,
        totalPAs: 0,
        totalFunded: 0,
        totalVolume: 0
    } );
    const [ view, setView ] = useState( 'leads' );
    const [ schema, setSchema ] = useState( {
        partner: {},
        team: {},
        company: {}
    } );
    const [ rows, setRows ] = useState( {
        partner: [],
        team: [],
        company: []
    } );

    const schemaCfg = useMemo( () => ( {
        partnersLeads: {
            cols: [
                { field: 'rank', headerName: '', width: 30 },
                { field: 'id', headerName: 'Partner', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.name; } },
                { field: 'value', headerName: 'Leads', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        partnersPAs: {
            cols: [
                { field: 'rank', headerName: '', width: 30 },
                { field: 'id', headerName: 'Partner', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.name; } },
                { field: 'value', headerName: 'Pre-Approvals', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        partnersFunded: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Partner', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.name; } },
                { field: 'value', headerName: 'Funded Loans', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        partnersVolume: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Partner', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.name; } },
                { field: 'value', headerName: 'Funded Volume', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{formatCurrencyShort( row.value )}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        teamsLeads: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Team', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.team; } },
                { field: 'value', headerName: 'Leads', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        teamsPAs: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Team', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.team; } },
                { field: 'value', headerName: 'Pre-Approvals', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        teamsFunded: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Team', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.team; } },
                { field: 'value', headerName: 'Funded Loans', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        teamsVolume: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Team', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.team; } },
                { field: 'value', headerName: 'Funded Volume', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{formatCurrencyShort( row.value )}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        companiesLeads: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Company', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.employer; } },
                { field: 'value', headerName: 'Leads', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        companiesPAs: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Company', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.employer; } },
                { field: 'value', headerName: 'Pre-Approvals', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        companiesFunded: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Company', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.employer; } },
                { field: 'value', headerName: 'Funded Loans', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{row.value}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
        companiesVolume: {
            cols: [
                { field: 'rank', headerName: '', width: 50 },
                { field: 'id', headerName: 'Company', flex: 1, renderCell: ( { row } ) => { return contacts[row.id]?.employer; } },
                { field: 'value', headerName: 'Funded Volume', width: 125, renderCell: ( { row } ) => { return <div><div className='fw-bold'>{formatCurrencyShort( row.value )}</div><div className='text-secondary fw-lighter' style={ { fontSize: '11px' } }>{parseFloat( ( row.share * 100 ).toFixed(1) )}%</div></div>} }
            ]
        },
    } ), [ contacts ] );

    const getLeaderboardData = useCallback( () => {
        const summary = {
            partners: {},
            teams: {},
            companies: {},
            totalLeads: 0,
            totalPAs: 0,
            totalFunded: 0,
            totalVolume: 0
        };
      
        // zero-out the summary...
        for ( const d of data ) {
            if ( !d.contact?.cid ) {
                continue;
            }
            if ( !summary.partners[d.contact.cid] ) {
                summary.partners[d.contact.cid] = {
                    leadCount: 0,
                    paCount: 0,
                    fundedCount: 0,
                    fundedVolume: 0
                }
            }

            if ( d.contact.team ) {
                if ( !summary.teams[d.contact.team] ) {
                    summary.teams[d.contact.team] = {
                        leadCount: 0,
                        paCount: 0,
                        fundedCount: 0,
                        fundedVolume: 0
                    }
                }
            }

            if ( d.contact.employer ) {
                if ( !summary.companies[d.contact.employer] ) {
                    summary.companies[d.contact.employer] = {
                        leadCount: 0,
                        paCount: 0,
                        fundedCount: 0,
                        fundedVolume: 0
                    }
                }
            }
        }

        for ( const d of data ) {
            if ( !d.contact?.cid ) {
                continue;
            }
            const pa = d.paDate || d.pqDate;
            const oppDate = new Date( d.oppDate?.match( 'T' ) ? d.oppDate : `${d.oppDate}T12:00:00` );
            const paDate = new Date( pa?.match( 'T' ) ? pa : `${pa}T12:00:00` );
            const fundedDate = new Date( d.fundedDate?.match( 'T' ) ? d.fundedDate : `${d.fundedDate}T12:00:00` );
            if ( isNaN( oppDate ) && isNaN( paDate ) && isNaN( fundedDate ) ) continue;
        
            if ( oppDate && oppDate >= filters.startDate && oppDate <= filters.endDate ) {
                summary.totalLeads++;
                summary.partners[d.contact.cid].leadCount++;

                if ( d.contact.team ) {
                    summary.teams[d.contact.team].leadCount++;
                }

                if ( d.contact.employer ) {
                    summary.companies[d.contact.employer].leadCount++;
                }
            }
            
            if ( paDate && paDate >= filters.startDate && paDate <= filters.endDate ) {
                summary.totalPAs++;
                summary.partners[d.contact.cid].paCount++;

                if ( d.contact.team ) {
                    summary.teams[d.contact.team].paCount++;
                }

                if ( d.contact.employer ) {
                    summary.companies[d.contact.employer].paCount++;
                }
            }
            
            if ( fundedDate && fundedDate >= filters.startDate && fundedDate <= filters.endDate ) {
                const amount = parseCurrencyToNumber( d.amount );

                summary.totalFunded++;
                summary.partners[d.contact.cid].fundedCount++;

                summary.totalVolume += amount;
                summary.partners[d.contact.cid].fundedVolume += amount;

                if ( d.contact.team ) {
                    summary.teams[d.contact.team].fundedCount++;
                    summary.teams[d.contact.team].fundedVolume += amount;
                }

                if ( d.contact.employer ) {
                    summary.companies[d.contact.employer].fundedCount++;
                    summary.companies[d.contact.employer].fundedVolume += amount;
                }
            }
        }

        return summary;
    }, [ data, filters.startDate, filters.endDate ] );

    useEffect( () => {
        if ( data?.length ) {
            const summary = getLeaderboardData();
            setSummaryData( summary );
            console.log( 'data:', data );
            console.log( 'summary:', summary );
        } else {
            setSummaryData( {
                partners: {},
                teams: {},
                companies: {},
                totalLeads: 0,
                totalPAs: 0,
                totalFunded: 0,
                totalVolume: 0
            } );
        }
    }, [ data, getLeaderboardData ] );

    useEffect( () => {
        const cids = Object.keys( summaryData.partners );
        const teams = Object.keys( summaryData.teams );
        const companies = Object.keys( summaryData.companies );

        const partnerTally = { leads: [], pas: [], funded: [], volume: [] };
        for ( const cid of cids ) {
            const row = summaryData.partners[cid];
            if ( row.leadCount > 0 ) {
                partnerTally.leads.push( { id: cid, value: row.leadCount, share: row.leadCount / summaryData.totalLeads } );
            }
            if ( row.paCount > 0 ) {
                partnerTally.pas.push( { id: cid, value: row.paCount, share: row.paCount / summaryData.totalPAs } );
            }
            if ( row.fundedCount > 0 ) {
                partnerTally.funded.push( { id: cid, value: row.fundedCount, share: row.fundedCount / summaryData.totalFunded } );
                partnerTally.volume.push( { id: cid, value: row.fundedVolume, share: row.fundedVolume / summaryData.totalVolume } );
            }
        }

        const teamTally = { leads: [], pas: [], funded: [], volume: [] };
        for ( const t of teams ) {
            const row = summaryData.teams[t];
            if ( row.leadCount > 0 ) {
                teamTally.leads.push( { id: t, value: row.leadCount, share: row.leadCount / summaryData.totalLeads } );
            }
            if ( row.paCount > 0 ) {
                teamTally.pas.push( { id: t, value: row.paCount, share: row.paCount / summaryData.totalPAs } );
            }
            if ( row.fundedCount > 0 ) {
                teamTally.funded.push( { id: t, value: row.fundedCount, share: row.fundedCount / summaryData.totalFunded } );
                teamTally.volume.push( { id: t, value: row.fundedVolume, share: row.fundedVolume / summaryData.totalVolume } );
            }
        }

        const companyTally = { leads: [], pas: [], funded: [], volume: [] };
        for ( const c of companies ) {
            const row = summaryData.companies[c];
            if ( row.leadCount > 0 ) {
                companyTally.leads.push( { id: c, value: row.leadCount, share: row.leadCount / summaryData.totalLeads } );
            }
            if ( row.paCount > 0 ) {
                companyTally.pas.push( { id: c, value: row.paCount, share: row.paCount / summaryData.totalPAs } );
            }
            if ( row.fundedCount > 0 ) {
                companyTally.funded.push( { id: c, value: row.fundedCount, share: row.fundedCount / summaryData.totalFunded } );
                companyTally.volume.push( { id: c, value: row.fundedVolume, share: row.fundedVolume / summaryData.totalVolume } );
            }
        }

        partnerTally.leads.sort( ( a, b ) => b.value - a.value );
        partnerTally.leads.forEach( ( f, idx ) => f.rank = idx + 1 );
        partnerTally.pas.sort( ( a, b ) => b.value - a.value );
        partnerTally.pas.forEach( ( f, idx ) => f.rank = idx + 1 );
        partnerTally.funded.sort( ( a, b ) => b.value - a.value );
        partnerTally.funded.forEach( ( f, idx ) => f.rank = idx + 1 );
        partnerTally.volume.sort( ( a, b ) => b.value - a.value );
        partnerTally.volume.forEach( ( f, idx ) => f.rank = idx + 1 );
        setPartners( partnerTally );

        teamTally.leads.sort( ( a, b ) => b.value - a.value );
        teamTally.leads.forEach( ( f, idx ) => f.rank = idx + 1 );
        teamTally.pas.sort( ( a, b ) => b.value - a.value );
        teamTally.pas.forEach( ( f, idx ) => f.rank = idx + 1 );
        teamTally.funded.sort( ( a, b ) => b.value - a.value );
        teamTally.funded.forEach( ( f, idx ) => f.rank = idx + 1 );
        teamTally.volume.sort( ( a, b ) => b.value - a.value );
        teamTally.volume.forEach( ( f, idx ) => f.rank = idx + 1 );
        setTeams( teamTally );

        companyTally.leads.sort( ( a, b ) => b.value - a.value );
        companyTally.leads.forEach( ( f, idx ) => f.rank = idx + 1 );
        companyTally.pas.sort( ( a, b ) => b.value - a.value );
        companyTally.pas.forEach( ( f, idx ) => f.rank = idx + 1 );
        companyTally.funded.sort( ( a, b ) => b.value - a.value );
        companyTally.funded.forEach( ( f, idx ) => f.rank = idx + 1 );
        companyTally.volume.sort( ( a, b ) => b.value - a.value );
        companyTally.volume.forEach( ( f, idx ) => f.rank = idx + 1 );
        setCompanies( companyTally );
    }, [ summaryData ] );

    useEffect( () => {
        const schema = {
            partner: {},
            team: {},
            company: {}
        };
        const rows = {
            partner: [],
            team: [],
            company: []
        };

        switch ( view ) {
            case 'pas':
                schema.partner = schemaCfg.partnersPAs;
                schema.team = schemaCfg.teamsPAs;
                schema.company = schemaCfg.companiesPAs;
                rows.partner = partners.pas;
                rows.team = teams.pas;
                rows.company = companies.pas;
                break;

            case 'funded-unit':
                schema.partner = schemaCfg.partnersFunded;
                schema.team = schemaCfg.teamsFunded;
                schema.company = schemaCfg.companiesFunded;
                rows.partner = partners.funded;
                rows.team = teams.funded;
                rows.company = companies.funded;
                break;

            case 'funded-vol':
                schema.partner = schemaCfg.partnersVolume;
                schema.team = schemaCfg.teamsVolume;
                schema.company = schemaCfg.companiesVolume;
                rows.partner = partners.volume;
                rows.team = teams.volume;
                rows.company = companies.volume;
                break;

            default:
                schema.partner = schemaCfg.partnersLeads;
                schema.team = schemaCfg.teamsLeads;
                schema.company = schemaCfg.companiesLeads;
                rows.partner = partners.leads;
                rows.team = teams.leads;
                rows.company = companies.leads;
        }

        setSchema( schema );
        setRows( rows );
        setLoaded( true );
    }, [ view, schemaCfg, companies, teams, partners ] );

    return (
        <Container fluid className='w-100'>
            { /** Leads **/ }
            <Row className='pb-4 text-center'>
                <Col className='px-0'>
                    <Card>
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col xs='auto'>
                                    <div className="segmented-nav mx-auto my-2">
                                        <Nav
                                            variant="pills"
                                            activeKey={view}
                                            className="gap-2 flex-nowrap overflow-auto"
                                        >
                                            <Nav.Item>
                                                <Nav.Link
                                                    // as={NavLink}      // if not using router: remove this and use href={it.to}
                                                    // to={it.to}
                                                    onClick={ () => setView( 'leads' ) }
                                                    eventKey='leads'
                                                    className="segmented-link"
                                                >
                                                    Leads
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link
                                                    // as={NavLink}      // if not using router: remove this and use href={it.to}
                                                    // to={it.to}
                                                    onClick={ () => setView( 'pas' ) }
                                                    eventKey='pas'
                                                    className="segmented-link"
                                                >
                                                    Pre-Approvals
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link
                                                    // as={NavLink}      // if not using router: remove this and use href={it.to}
                                                    // to={it.to}
                                                    onClick={ () => setView( 'funded-unit' ) }
                                                    eventKey='funded-unit'
                                                    className="segmented-link"
                                                >
                                                    Funded Units
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link
                                                    // as={NavLink}      // if not using router: remove this and use href={it.to}
                                                    // to={it.to}
                                                    onClick={ () => setView( 'funded-vol' ) }
                                                    eventKey='funded-vol'
                                                    className="segmented-link"
                                                >
                                                    Funded Volume
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                            {
                                [
                                    { key: `partner-${view}`, schema: schema.partner, rows: rows.partner },
                                    { key: `team-${view}`, schema: schema.team, rows: rows.team },
                                    { key: `company-${view}`, schema: schema.company, rows: rows.company },
                                ].map( ( cfg ) => (
                                    <Col key={cfg.key} className='d-flex flex-column' style={ { minWidth: 0 } }>
                                        <div className="d-flex" style={{ height: '400px', overflow: 'auto', minWidth: 0 }}>
                                            <ThirdFloorDataGrid gridKey={cfg.key} schema={ cfg.schema } registry={ {} } rows={cfg.rows} overrides={ { hideFooter: true } } />  
                                        </div>
                                    </Col>
                                ) )
                            }
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
  }

  const mapProps = ( state ) => ( {
    contacts: state.contacts.byId,
} );
  
const mapDispatch = ( dispatch ) => ( {
} );
  
export default connect( mapProps, mapDispatch )( Leaderboards );